import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {useParams} from 'react-router-dom';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {ProjectForm} from 'src/app/projects/ProjectForm';
import {SidebarLinkProjectsAdmin} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetProjectQuery} from 'src/redux/queries/project';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {ProjectAssignmentRulesTab} from './components/AssignmentRules/ProjectAssignmentRulesTab';
import {ProjectRosterTab} from './components/Roster/ProjectRosterTab';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type ProjectViewTab = 'details' | 'assign' | 'roster';

export const ViewProjectPage = () => {
  useSetDocumentTitle('View Project');
  const navigate = useAppNavigate();
  const {id, view} = useParams();
  const perms = useModulePermissions(ModuleEnum.Projects);

  const validId = id !== undefined && !isNaN(+id) ? +id : undefined;

  const {
    data: project,
    isLoading,
    isError,
  } = useGetProjectQuery(validId!, {
    skip: validId === undefined,
  });

  const {RenderedTabsComponent} = useUrlQueryTabV2<ProjectViewTab>({
    view: (view ?? '') as ProjectViewTab,
    defaultView: 'details',
    tabs: [
      {
        view: 'details',
        label: 'Details',
        component: validId !== undefined && (
          <ProjectForm id={validId} mode="view" embedded />
        ),
      },
      {
        view: 'assign',
        label: 'Assignment Rules',
        component: validId !== undefined && (
          <ProjectAssignmentRulesTab projectId={validId} />
        ),
      },
      {
        view: 'roster',
        label: 'Roster',
        component: validId !== undefined && (
          <ProjectRosterTab projectId={validId} />
        ),
      },
    ],
    onSelectView: nextView =>
      navigate(PathUtility.ProjectView(id as string, nextView), {replace: true}),
  });

  if (isLoading) {
    return (
      <ContentWrapper>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (isError || !project) {
    return (
      <ContentWrapper>
        <Alert color="red" title="Not Found">
          Project not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.Project('list')}
          fallbackLabel="Back to Projects"
          breadcrumbs={[
            {label: 'Projects', to: PathUtility.Project('list')},
            {label: project.name ?? 'Project'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkProjectsAdmin.icon}
            subtitle={project.code ?? undefined}>
            {project.name ?? 'Project'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <PageHeader.Badge
              color={project.active ? 'green' : 'gray'}
              variant="light">
              {project.active ? 'Active' : 'Inactive'}
            </PageHeader.Badge>
          </PageHeader.Badges>

          <PageHeader.Actions>
            {perms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() => navigate(PathUtility.ProjectEdit(id!))}>
                Edit
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        {RenderedTabsComponent}
      </Stack>
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ProjectView(':id', ':view?'),
  element: <ViewProjectPage />,
  module: ModuleEnum.Projects,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
  allowGlobalAdminBypass: true,
});

export default ViewProjectPage;
